// ScenarioDependencyPicker — search-as-you-type scenario selector used wherever a
// dependency target is chosen (Add Dependency modal, Create Scenario modal).
// Live mode pages vw_Scenarios via GraphQL with cursor-based infinite scroll and
// server-side search on Name; demo mode filters a local array client-side.
//
// Only scenarios the user owns or that are explicitly shared with them are listed —
// purely inherited (status-conditional/broadcast) access is filtered out client-side
// since vw_Scenarios returns those too.

import { ChevronDown, Loader2, Search, X } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type {
  ScenarioViewFilterInput,
  ScenarioViewNode,
} from "@/api/models/scenarioView";
import {
  getScenariosGraphql,
  type ScenarioViewSortInput,
} from "@/api/services/scenarioGraphqlService";
import { formatStatus } from "@/components/forecast/ScenarioOverlayPicker";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { AppMode } from "@/providers/ModeProvider";
import type { ComprehensiveScenario, ScenarioStatus } from "@/types/scenarios";

export interface PickerScenario {
  id: string;
  name: string;
  status: ScenarioStatus;
}

interface ScenarioDependencyPickerProps {
  /** Selected scenario id ('' when nothing is chosen). */
  value: string;
  onChange: (id: string) => void;
  /** Demo vs live. In demo mode the picker never calls GraphQL. */
  mode: AppMode;
  /** Scenario ids to hide — e.g. the current scenario and already-linked targets. */
  excludeIds?: string[];
  /** Local scenarios used for client-side filtering in demo mode. */
  demoScenarios?: ComprehensiveScenario[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const PAGE_SIZE = 50;

const SORT: ScenarioViewSortInput[] = [{ field: "Name", order: "Ascending" }];

/** Owner or explicit share only — inherited/broadcast access is not selectable. */
function hasDirectAccess(node: ScenarioViewNode): boolean {
  return node.isOwner === true || node.isShared === true;
}

function ScenarioRow({
  scenario,
  isSelected,
  onSelect,
}: {
  scenario: PickerScenario;
  isSelected: boolean;
  onSelect: (scenario: PickerScenario) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(scenario)}
      className={cn(
        "w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md text-left transition-colors",
        "hover:bg-accent focus:outline-none focus:bg-accent",
        isSelected && "bg-primary/5 ring-1 ring-primary/20",
      )}
    >
      <span className="flex-1 min-w-0 truncate text-sm font-medium">
        {scenario.name}
      </span>
      <Badge variant="outline" className="flex-shrink-0">
        {formatStatus(scenario.status)}
      </Badge>
    </button>
  );
}

export function ScenarioDependencyPicker({
  value,
  onChange,
  mode,
  excludeIds,
  demoScenarios = [],
  placeholder,
  disabled = false,
  className,
}: ScenarioDependencyPickerProps) {
  const isLive = mode === "live";
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<PickerScenario | null>(null);

  // Live-mode fetching state
  const [liveList, setLiveList] = useState<PickerScenario[]>([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchingRef = useRef(false);
  const queryRef = useRef("");

  const resolvedPlaceholder = placeholder ?? "Search by name…";

  const buildFilter = useCallback(
    (search: string): ScenarioViewFilterInput | undefined => {
      const trimmed = search.trim();
      return trimmed ? { name: { contains: trimmed } } : undefined;
    },
    [],
  );

  const mapNodes = useCallback(
    (nodes: ScenarioViewNode[]): PickerScenario[] =>
      nodes.filter(hasDirectAccess).map((node) => ({
        id: String(node.id),
        name: node.name,
        status: node.status as ScenarioStatus,
      })),
    [],
  );

  const loadFirstPage = useCallback(
    async (search: string) => {
      if (!isLive) return;
      fetchingRef.current = true;
      setInitialLoading(true);
      try {
        const conn = await getScenariosGraphql(
          buildFilter(search),
          PAGE_SIZE,
          undefined,
          SORT,
        );
        setLiveList(mapNodes(conn.edges.map((e) => e.node)));
        setHasNextPage(conn.pageInfo.hasNextPage);
        setEndCursor(conn.pageInfo.endCursor);
      } catch (err) {
        console.error("Failed to fetch scenarios:", err);
        setLiveList([]);
        setHasNextPage(false);
        setEndCursor(null);
      } finally {
        setInitialLoading(false);
        fetchingRef.current = false;
      }
    },
    [isLive, buildFilter, mapNodes],
  );

  const loadNextPage = useCallback(async () => {
    if (!isLive || fetchingRef.current || !hasNextPage || !endCursor) return;
    fetchingRef.current = true;
    setLoadingMore(true);
    try {
      const conn = await getScenariosGraphql(
        buildFilter(queryRef.current),
        PAGE_SIZE,
        endCursor,
        SORT,
      );
      setLiveList((prev) => [
        ...prev,
        ...mapNodes(conn.edges.map((e) => e.node)),
      ]);
      setHasNextPage(conn.pageInfo.hasNextPage);
      setEndCursor(conn.pageInfo.endCursor);
    } catch (err) {
      console.error("Failed to fetch more scenarios:", err);
    } finally {
      setLoadingMore(false);
      fetchingRef.current = false;
    }
  }, [isLive, hasNextPage, endCursor, buildFilter, mapNodes]);

  const isExcluded = useCallback(
    (id: string) => !!excludeIds && id !== value && excludeIds.includes(id),
    [excludeIds, value],
  );

  // Demo mode: client-side name search over the visible scenarios.
  const filteredDemo: PickerScenario[] = demoScenarios
    .filter(
      (s) =>
        !query.trim() ||
        s.name.toLowerCase().includes(query.trim().toLowerCase()),
    )
    .map((s) => ({ id: s.id, name: s.name, status: s.status }));

  const displayList = (isLive ? liveList : filteredDemo).filter(
    (s) => !isExcluded(s.id),
  );

  const handleQueryChange = useCallback(
    (q: string) => {
      setQuery(q);
      queryRef.current = q;
      if (!isLive) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setEndCursor(null);
        setHasNextPage(false);
        setLiveList([]);
        loadFirstPage(q);
      }, 300);
    },
    [isLive, loadFirstPage],
  );

  const handleOpen = () => {
    if (disabled) return;
    // Clicking the already-open control (e.g. inside the input) must not reset the query.
    if (open) {
      searchRef.current?.focus();
      return;
    }
    setOpen(true);
    setQuery("");
    queryRef.current = "";
    if (isLive) loadFirstPage("");
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  const handleSelect = (scenario: PickerScenario) => {
    setSelected(scenario);
    onChange(scenario.id);
    setOpen(false);
    setQuery("");
    queryRef.current = "";
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected(null);
    onChange("");
  };

  const handleScroll = () => {
    if (!isLive) return;
    const el = listRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      loadNextPage();
    }
  };

  // Keep the trigger label in sync when the parent clears or changes the value.
  const currentList = isLive ? liveList : filteredDemo;
  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    const found = currentList.find((s) => s.id === value);
    if (found && found.id !== selected?.id) setSelected(found);
  }, [value, currentList, selected]);

  // A page can be emptied entirely by the owned/shared filter, leaving the list too
  // short to scroll — keep pulling pages until there is enough to scroll on.
  useEffect(() => {
    if (!open || !isLive) return;
    if (initialLoading || loadingMore || !hasNextPage) return;
    if (displayList.length >= 10) return;
    loadNextPage();
  }, [
    open,
    isLive,
    initialLoading,
    loadingMore,
    hasNextPage,
    displayList.length,
    loadNextPage,
  ]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Cleanup debounce
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Trigger — doubles as the search input, so there is only ever one search box */}
      <div
        onClick={handleOpen}
        className={cn(
          "w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-input bg-background",
          "transition-colors text-left",
          disabled ? "cursor-not-allowed opacity-70" : "cursor-text",
          open && "ring-2 ring-ring",
        )}
      >
        <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        {selected && !open ? (
          <>
            <span className="flex-1 truncate font-medium">{selected.name}</span>
            <Badge variant="outline" className="flex-shrink-0">
              {formatStatus(selected.status)}
            </Badge>
            {!disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="ml-1 p-0.5 rounded hover:bg-muted-foreground/20 text-muted-foreground flex-shrink-0"
                title="Clear selection"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </>
        ) : (
          <input
            ref={searchRef}
            type="text"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            placeholder={selected ? selected.name : resolvedPlaceholder}
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setOpen(false);
            }}
            className={cn(
              "flex-1 min-w-0 bg-transparent outline-none placeholder:text-muted-foreground",
              disabled && "cursor-not-allowed",
            )}
          />
        )}
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform flex-shrink-0",
            open && "rotate-180",
          )}
        />
      </div>

      {/* Dropdown */}
      {open && (
        <div
          className={cn(
            "absolute z-50 mt-1 w-full min-w-[280px] rounded-lg border border-border bg-popover shadow-lg",
            "animate-in fade-in-0 zoom-in-95",
          )}
        >
          {/* Results */}
          <div
            ref={listRef}
            onScroll={handleScroll}
            className="overflow-y-auto max-h-72 py-1 px-1"
          >
            {isLive && initialLoading ? (
              <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : displayList.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {query ? `No results match "${query}"` : "Nothing available"}
              </div>
            ) : (
              <>
                {displayList.map((scenario) => (
                  <ScenarioRow
                    key={scenario.id}
                    scenario={scenario}
                    isSelected={scenario.id === value}
                    onSelect={handleSelect}
                  />
                ))}
                {loadingMore && (
                  <div className="flex items-center justify-center py-3 gap-2 text-muted-foreground text-xs">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading…
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
